// Translation dictionary for the app's core, highest-traffic surfaces (navigation, the
// Home POS terminal sell flow, cart, payment, and Settings itself). Other pages fall back
// to their hardcoded English strings until translated — see LanguageContext's t().
const translations = {
  en: {
    nav: {
      register: "Register",
      inventory: "Inventory",
      stockAdjustments: "Stock Adjustments",
      salesHistory: "Sales History",
      creditDebit: "Credit / Debit",
      contacts: "Contacts",
      storeCredit: "Store Credit",
      paymentMediums: "Payment Mediums",
      shifts: "Shifts",
      report: "Sales Report",
      company: "Company",
      settings: "Settings",
      devices: "Devices",
      support: "Support",
    },
    update: {
      codeReady: "Version {version} is ready. Restart the app to use it.",
      appReady: "A new version of the app ({version}) is ready to install.",
      restart: "Restart now",
      install: "Install",
      later: "Later",
      allowInstall: "Allow installing apps from POS on the screen that opened, then tap Install again.",
      installFailed: "The update couldn't be opened for installing.",
      check: "Check for updates",
      version: "Version {version}",
      upToDate: "You have the latest version.",
      found: "Version {version} found.",
      downloading: "Downloading version {version}…",
      checkFailed: "Couldn't check for updates: {message}",
    },
    support: {
      pageTitle: "Support",
      introOwner: "Report a problem or ask for a feature, then follow its status here. You see every ticket from your shop.",
      introStaff: "Report a problem or ask for a feature, then follow its status here. You see the tickets you opened.",
      newTicket: "New ticket",
      reportProblem: "Report a problem / suggest",
      kindBug: "Bug",
      kindFeature: "Feature request",
      kindBugHint: "Something isn't working as it should",
      kindFeatureHint: "Something new you'd like the app to do",
      title: "Title",
      titleBugPlaceholder: "e.g. Receipt prints twice",
      titleFeaturePlaceholder: "e.g. Send receipts on WhatsApp",
      description: "Description",
      bodyBugPlaceholder: "What happened, what you expected, and the steps to make it happen again.",
      bodyFeaturePlaceholder: "What you'd like, and how it would help your shop.",
      pictures: "Screenshots (optional)",
      addPicture: "Add picture",
      removePicture: "Remove picture",
      picturesMax: "At most {n} pictures per message.",
      pictureTooBig: "That picture is too big even after shrinking it.",
      notAPicture: "That file isn't a picture.",
      submit: "Send ticket",
      sending: "Sending...",
      created: "Ticket sent. Its status will show here as it's worked on.",
      includeLogs: "Send diagnostic logs (recommended)",
      includeLogsHint: "What the app recorded in the last {minutes} minutes ({n} entries): pages opened, requests and whether they worked, errors and messages shown. No passwords and no sale details.",
      empty: "No tickets here yet.",
      filter_active: "Open",
      filter_closed: "Closed",
      filter_all: "All",
      newReply: "Status updated",
      back: "All tickets",
      report: "What was reported",
      statusHistory: "Status history",
      historyOpened: "Opened by {name}",
      wontFixNote: "The support team decided not to take this one forward.",
      diagnosticsSent: "Diagnostic logs were sent with this ticket.",
      diagnosticsNotSent: "No diagnostic logs were sent with this ticket.",
      status_open: "Open",
      status_in_review: "In review",
      status_planned: "Planned",
      status_in_progress: "In progress",
      status_resolved: "Resolved",
      status_wont_fix: "Won't do",
      badge: "Status updates on your support tickets",
      problemPromptTitle: "Something went wrong",
      problemPromptBody: "Report it so it can be fixed? What the app recorded just now goes with the report.",
      problemReport: "Report it",
      problemNotNow: "Not now",
      problemDismiss: "Dismiss",
      problemTitle: "The app showed an error",
      problemNotice: "The error just now was recorded and goes with the diagnostic logs. Tell us what you were doing when it happened.",
      problemBodyPlaceholder: "What were you doing when it happened? e.g. I pressed Pay on a sale with 3 items.",
      crashTitle: "Something went wrong on this page",
      crashBody: "Reload to carry on. If it keeps happening, report it — what happened is sent with the report.",
      crashReload: "Reload",
      crashReport: "Report the problem",
    },
    common: {
      add: "Add",
      edit: "Edit",
      delete: "Delete",
      update: "Update",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving...",
      submit: "Submit",
      loading: "Loading…",
    },
    pos: {
      addProduct: "Add Product",
      addCategory: "Add Category",
      update: "Update",
    },
    register: {
      title: "Register",
      all: "All",
      searchPlaceholder: "Search product or scan lot code",
      manage: "Manage",
      editProducts: "Edit products",
      shiftOpen: "Shift open",
      noShift: "No shift open",
      noShiftWarning: "No shift open — this sale can't be completed until you open one.",
      shiftOpenLong: "Shift open since {since} · count and close",
      left: "{n} left",
      outOfStock: "Out of stock",
      lots: "Lots",
      setPrice: "Set price",
      noResults: "No products match “{q}”.",
      lotMatch: "Lot {code}",
      gridView: "Grid view",
      listView: "List view",
      emptyCatalog: "No products yet.",
      onlyNLeft: "Only {n} in stock.",
      currentSale: "Current sale",
      itemsCount: "{n} items",
      oneItem: "1 item",
      pay: "Pay",
      hold: "Hold",
      held: "Held",
      heldTitle: "Held sales",
      heldEmpty: "No held sales.",
      resume: "Resume",
      discard: "Discard",
      clearSale: "Clear",
      clearConfirm: "Remove all items from this sale?",
      heldSaved: "Sale held — resume it anytime from Held.",
      heldFull: "You can hold up to {n} sales at a time.",
      belowCost: "Below cost",
      lastSold: "Last sold at",
      lastShort: "Set price · last {amount}",
      alreadyInSale: "Already in this sale. Add one more at the same price, or at a new one?",
      samePrice: "Same price · {amount}",
      newPrice: "New price",
      margin: "Margin",
      each: "each",
      editPrice: "Edit price",
      chooseLot: "Choose lot",
      completeSale: "Complete sale",
      exact: "Exact",
      received: "Received",
      shortcuts: "F2 search · F4 pay · Esc close",
    },
    sell: {
      inStock: "In stock",
      costPrice: "Cost price",
      quantity: "Quantity",
      sellingPrice: "Selling price",
      quantityError: "Enter a quantity between 1 and {max}.",
      cancel: "Cancel",
      addToCart: "Add to Cart",
    },
    cart: {
      empty: "Your cart is empty.",
      emptyHint: "Tap a product or scan a lot code to start a sale.",
      subtotal: "Subtotal",
      remove: "Remove",
    },
    payment: {
      title: "Payment",
      amountDue: "Amount due",
      cash: "Cash",
      card: "Card",
      amountReceived: "Amount received",
      amountReceivedPlaceholder: "Enter cash received",
      changeDue: "Change due",
      amountShort: "Amount short",
      cancel: "Cancel",
      confirmSale: "Confirm Sale",
      processing: "Processing...",
      haveVoucherCode: "Have a store credit voucher code?",
      hideStoreCredit: "Hide voucher code",
      voucherCode: "Voucher code",
      offlineVoucherTitle: "Owner's approval needed",
      offlineVoucherBody:
        "This register is offline, so it can't check whether this voucher was already spent on another register. The owner can allow it by entering their password.",
      ownerPassword: "Owner's password",
      approve: "Approve",
      invalidVoucherCode: "Invalid or unrecognized voucher code.",
      loadingBalance: "Loading balance...",
      availableCredit: "Available credit",
      amountToRedeem: "Amount to redeem",
      storeCreditApplied: "Store credit applied",
      fullyCoveredByCredit: "Fully covered by store credit — nothing else due.",
      bankTransfer: "Bank Transfer",
      bankTransferUnavailableOffline: "Bank transfer needs a connection — unavailable offline",
      scanToPay: "Scan with a banking app to pay",
      reference: "Reference",
      waitingForConfirmation: "Waiting for payment — mark as paid once it's received",
      markAsPaid: "Mark as Paid",
      close: "Close",
    },
    receipt: {
      title: "Receipt",
      receiptTitle: "RECEIPT",
      receiptNo: "Receipt No",
      noItems: "No items",
      total: "TOTAL",
      storeCreditApplied: "Store Credit Applied",
      amountPaid: "Amount Paid",
      thankYou: "Thank you for your purchase!",
      done: "Done",
      print: "Print",
      printing: "Printing...",
      printError: "Couldn't print the receipt.",
    },
    time: {
      never: "never",
      justNow: "just now",
      minutesAgo: "{n} min ago",
      hoursAgo: "{n} h ago",
      dayAgo: "1 day ago",
      daysAgo: "{n} days ago",
    },
    devices: {
      title: "Devices",
      intro:
        "Registers that keep selling without internet: the Windows app on a PC, the Android app on a phone. Each one sends its sales here when it's online.",
      howToAdd: "To add one, install the POS app on the PC or phone and sign in there with your owner account.",
      limit: "{used} of {limit} devices in use",
      none: "No devices yet.",
      lastSync: "Last sync",
      waiting: "{n} waiting to send",
      waitingAsOf: "as of last contact",
      registeredBy: "Added {when} by {name}",
      clockOff: "This device's clock is {minutes} min off — fix its date and time.",
      retire: "Retire",
      block: "Block",
      retireConfirm:
        "Retire {name}? It stops syncing at once. Use this when the device is replaced; anything it hasn't sent yet stays on it.",
      blockConfirm: "Block {name}? Use this if it was lost or stolen: it is cut off at once and anything it sends is refused.",
      retired: "{name} retired.",
      blocked: "{name} blocked.",
      state: {
        in_sync: "In sync",
        behind: "Behind",
        offline_long: "Offline for long",
        retired: "Retired",
        blocked: "Blocked",
      },
      issuesTitle: "Sync issues",
      issuesIntro: "Changes a device made that couldn't be saved here. Check each one in the app, then mark it handled.",
      noIssues: "No sync issues.",
      resolve: "Mark handled",
    },
    deviceSync: {
      synced: "Synced {when}",
      syncing: "Syncing…",
      offline: "Offline",
      waiting: "{n} waiting",
      syncNow: "Sync now",
      warn: "No internet for {days} days — connect this register so its sales reach the owner.",
      blocked: "This register has been offline for {days} days. Connect it to the internet to sync, then carry on selling.",
      revoked: "This register was retired or blocked by the owner and no longer syncs.",
    },
    deviceSetup: {
      title: "Set up this device",
      intro: "Sign in with the shop owner's account. This device will download the shop and then work even without internet.",
      ownerUsername: "Owner's username",
      deviceName: "Name for this device",
      deviceNameHint: "e.g. Counter PC or Ali's phone — shown on the owner's Devices list.",
      server: "Server address",
      changeServer: "Change server address",
      submit: "Set up device",
      needsInternet: "Needs internet once, for setup.",
      done: "This device is ready for {shop}. Sign in to start.",
      step: {
        signing_in: "Signing in",
        registering: "Registering this device",
        downloading: "Downloading the shop",
        saving: "Saving on this device",
      },
    },
    auth: {
      title: "Sign In",
      deviceShop: "This register belongs to {shop}. Only its staff can sign in here.",
      username: "Username",
      password: "Password",
      login: "Log In",
      loggingIn: "Logging in...",
      loginError: "Something went wrong. Please try again.",
      logout: "Log Out",
      roleOwner: "Owner",
      roleCashier: "Cashier",
      usersTitle: "Users",
      usersDesc: "Owner and Cashier accounts for this till. Deactivating an account blocks login without deleting their sales history.",
      addUser: "Add User",
      displayName: "Display Name",
      role: "Role",
      userAdded: "User added",
      you: "you",
      active: "Active",
      inactive: "Inactive",
      cantDeactivateSelf: "You can't deactivate your own account",
      editUser: "Edit User",
      userUpdated: "User updated",
      newPasswordOptional: "New Password (optional)",
      newPasswordPlaceholder: "Leave blank to keep current password",
      newPasswordHint: "Only fill this in if you want to change the password.",
      userLimitReached: "User limit reached ({max}) — ask your provider to raise it to add more users.",
      usersOfLimit: "{count} of {max} users",
      forgotPassword: "Forgot password?",
      forgotPasswordTitle: "Request a Password Reset",
      forgotPasswordDesc:
        "Enter your username and at least your CNIC or phone number so the platform admin can verify it's really you before resetting your password.",
      forgotPasswordCnic: "CNIC (optional)",
      forgotPasswordPhone: "Phone number (optional)",
      forgotPasswordIdentityHint: "Provide at least one of CNIC or phone.",
      forgotPasswordSubmit: "Submit Request",
      forgotPasswordSubmitting: "Submitting...",
      forgotPasswordSuccess: "If we found a matching account, your request has been submitted to the platform admin.",
      setNewPasswordTitle: "Set a New Password",
      setNewPasswordDesc: "Your password was reset by the platform admin. Choose a new one to continue.",
      newPassword: "New Password",
      confirmNewPassword: "Confirm New Password",
      newPasswordMismatch: "New password and confirmation don't match.",
      changePassword: "Change password",
      currentPassword: "Current Password",
      passwordMinLength: "At least 8 characters",
      passwordChanged: "Password changed. Use the new one from now on, on every device.",
      setNewPasswordSubmit: "Set Password",
      setNewPasswordSubmitting: "Saving...",
    },
    settings: {
      title: "Settings",
      loading: "Loading settings...",
      lowStockTitle: "Low Stock Threshold",
      lowStockDesc:
        'Is quantity se neeche aane par product ko "Low Stock" mark kiya jayega (Inventory page aur sale ke waqt dono jagah).',
      batchNote: 'Lot/batch tracking ab har product ke liye "Add Product" mein alag se on kiya jata hai.',
      languageTitle: "Language",
      languageDesc: "Poore app ka interface language yahan se badla ja sakta hai.",
      textSizeTitle: "Text size",
      textSizeDesc: "How big text and buttons are on this device. L is the standard size; pick a smaller one to fit more on a phone screen.",
      textSize_xs: "Extra small",
      textSize_sm: "Small",
      textSize_md: "Medium",
      textSize_lg: "Large (standard)",
      textSize_xl: "Extra large",
      english: "English",
      urdu: "اردو",
      receiptPrinterTitle: "Receipt Printer",
      testPrint: "Test Print",
      connectBluetooth: "Connect Bluetooth Printer",
      connectUsb: "Connect USB Printer",
      disconnect: "Disconnect",
      forwarderTitle: "Bank SMS Forwarder",
      forwarderDesc:
        "The Android forwarder app sends this shop's bank and JazzCash payment alerts here, so bank-transfer sales confirm themselves. Each shop has its own secret — type it into the phone app's Secret field.",
      forwarderConfiguredOn: "Secret created",
      forwarderNotConfigured: "No secret yet — the phone app can't connect until you generate one.",
      forwarderGenerate: "Generate secret",
      forwarderRegenerate: "Generate new secret",
      forwarderRegenerateConfirm:
        "The phone app will stop confirming payments until you enter the new secret in it. Continue?",
      forwarderSecretOnce: "Copy this now and enter it in the phone app — it won't be shown again.",
      forwarderGenerated: "New forwarder secret created",
      copy: "Copy",
      copied: "Copied to clipboard",
      receiptTermsTitle: "Receipt Terms & Conditions",
      receiptTermsDesc: "This text prints exactly as written at the bottom of the receipt. Each line prints separately.",
      refundWindowTitle: "Refund Window (days)",
      refundWindowDesc: "Sales older than this many days can't be refunded. Leave blank for no limit.",
      refundWindowUnlimited: "Unlimited",
      refundWindowUpdated: "Refund window updated",
      timezoneTitle: "Timezone",
      timezoneDesc: "Every date/time shown or printed anywhere in the app — receipts, Sales History, reports — uses this timezone, the same for every device, not each viewer's own.",
      timezoneAuto: "Auto",
      timezoneUpdated: "Timezone updated",
    },
    company: {
      title: "Company",
      detailsTitle: "Company Details",
      detailsDesc: "These details print on the receipt/slip — name and logo in the header, the rest below.",
      companyName: "Company Name",
      ntnNumber: "NTN Number",
      phone: "Phone",
      address: "Address",
      logo: "Logo",
      optional: "Optional",
      upload: "Upload",
      change: "Change",
      saveChanges: "Save Changes",
      loading: "Loading company details...",
      bankDetailsTitle: "Bank Account (for QR payments)",
      bankDetailsDesc: "Shown on the QR code customers scan to pay by bank transfer.",
      bankName: "Bank Name",
      selectBank: "Select a bank",
      otherBank: "Other / not listed",
      otherBankPlaceholder: "Enter bank name",
      bankAccountTitle: "Account Title",
      bankAccountNumber: "Account Number",
      bankIban: "IBAN",
      bankIbanHint: "Required — this is what actually goes into the QR (Pakistan's Raast standard pays by IBAN, not account number). Wallets like JazzCash/Easypaisa have one too, even though their app doesn't show it up front — check the app's account details or call their helpline.",
    },
    contacts: {
      title: "Contacts",
      addContact: "Add Contact",
      all: "All",
      vendors: "Vendors",
      customers: "Customers",
      empty: "No contacts yet. Add a customer or vendor.",
      noDetails: "No contact details.",
      vendor: "Vendor",
      customer: "Customer",
    },
    creditDebit: {
      title: "Credit / Debit",
      pendingPayables: "Pending Payables",
      pendingPayablesInfo: "Total amount you currently owe, combined across all suppliers.",
      pendingReceivables: "Pending Receivables",
      pendingReceivablesInfo: "Total amount currently owed to you, combined across all customers.",
      netPosition: "Net Position",
      netPositionInfo:
        "Receivables minus payables. If everyone settled up today, this is what you'd net receive (positive) or net pay (negative).",
      totalOutstanding: "Total Outstanding",
      totalOutstandingInfo:
        "Payables plus receivables — the total amount currently outstanding across your whole ledger.",
      payableTitle: "Payable — You owe suppliers",
      receivableTitle: "Receivable — Customers owe you",
      addPayable: "Add Payable",
      addReceivable: "Add Receivable",
      noPayables: "No payables recorded.",
      noReceivables: "No receivables recorded.",
      supplier: "Supplier",
      customer: "Customer",
      due: "Due",
      paid: "Paid",
      pending: "Pending",
      settle: "Settle",
      addCharge: "Add Charge",
      netOff: "Net Off",
      netOffDone: "Net off recorded!",
      netOffExplain: "Books this amount as paid off on both sides — no cash moves.",
      payableBalance: "Payable",
      receivableBalance: "Receivable",
      amount: "Amount",
      date: "Date",
      type: "Type",
      note: "Note",
      optional: "optional",
      addEntry: "Add Entry",
      saveChanges: "Save Changes",
      selectCustomer: "Select a customer",
      selectVendor: "Select a vendor",
      entryAdded: "Entry added!",
      paymentRecorded: "Payment recorded!",
      enterValidAmount: "Enter an amount greater than 0.",
      history: "History",
      loadingHistory: "Loading history...",
      noHistory: "No entries yet.",
      runningBalance: "Balance",
      chargeReceivable: "Credit Given",
      paymentReceivable: "Payment Received",
      chargePayable: "Purchased on Credit",
      paymentPayable: "Payment Made",
      adjustment: "Adjustment",
      entryUpdated: "Entry updated!",
      entryDeleted: "Entry deleted!",
      confirmDeleteEntry: "Delete this entry? This can't be undone.",
      addChargeInfo:
        "Records new money owed — you owe a supplier more (Payable), or a customer owes you more (Receivable).",
      settleInfo: "Records a payment — full or partial — that reduces what's owed on this side.",
      netOffInfo:
        "For a party who both owes you and is owed by you — books this amount as settled on both sides at once, without moving actual cash.",
    },
    inventory: {
      title: "Inventory",
      totalSkus: "Total SKUs",
      totalStockValue: "Total Stock Value",
      lowStock: "Low Stock",
      outOfStock: "Out of Stock",
      searchPlaceholder: "Search by name...",
      allCategories: "All categories",
      allStatuses: "All statuses",
      inStock: "In Stock",
      product: "Product",
      category: "Category",
      qty: "Qty",
      price: "Price",
      value: "Value",
      status: "Status",
      noMatch: "No products match these filters.",
      noLots: "No lots recorded.",
      loadingLots: "Loading lots...",
      lotCode: "Lot Code",
      vendor: "Vendor",
      remaining: "Remaining",
      received: "Received",
      adjustStock: "Adjust Stock",
      adjustStockTitle: "Adjust Stock",
      adjustStockLot: "Lot",
      adjustStockQuantityChange: "Quantity Change",
      adjustStockQuantityHint: "Positive to add (a correction-up), negative to remove (shrinkage/loss)",
      adjustStockReason: "Reason",
      adjustStockReasonRestock: "Restock (received more stock)",
      adjustStockReasonDamaged: "Damaged",
      adjustStockReasonExpired: "Expired",
      adjustStockReasonTheft: "Theft",
      adjustStockReasonCountCorrection: "Count Correction",
      adjustStockReasonOther: "Other",
      adjustStockNote: "Note (optional)",
      adjustStockSubmit: "Record Adjustment",
      adjustStockSuccess: "Stock adjustment recorded",
      restock: "Restock",
      restockTitle: "Restock",
      restockQuantity: "Units received",
      restockQuantityHint: "How many units arrived. They're added to stock and recorded under Stock Adjustments.",
      restockSubmit: "Add to stock",
      inStockNow: "{qty} in stock now",
      stockAlerts: "Stock Alerts",
      stockAlertsEmpty: "Everything is well stocked.",
      stockAlertsHint: "Click a product to add stock.",
      unitsLeft: "{qty} left",
      openInventory: "Open Inventory",
      quantityLockedHint: "Quantity can't be edited here — use \"Adjust Stock\" to change it, up or down, with a reason.",
    },
    stockAdjustments: {
      title: "Stock Adjustments",
      allReasons: "All Reasons",
      date: "Date",
      quantityChange: "Quantity Change",
      adjustedBy: "Adjusted By",
      empty: "No stock adjustments recorded yet.",
      note: "Note",
      traceability: "Traceability",
      lotReceivedAt: "Lot Received",
      lotReceivedBy: "Accepted By",
      filteredByProduct: "Filtered by product",
    },
    salesHistory: {
      title: "Sales History",
      startDate: "Start Date:",
      endDate: "End Date:",
      category: "Category:",
      allCategories: "All Categories",
      clearFilter: "Clear filter",
      dateTime: "Date/Time",
      receiptNo: "Receipt No",
      items: "Items",
      total: "Total",
      loading: "Loading...",
      empty: "No past sales found.",
      print: "Print",
      buyingPrice: "Buying Price",
      sellingPrice: "Selling Price",
      lineTotal: "Line Total",
      void: "Void",
      voidedBadge: "Voided",
      remoteSale: "Older sale: void or refund it from the web app",
      voided: "Sale voided — stock restored.",
      voiding: "Voiding...",
      voidConfirmTitle: "Void this sale?",
      voidConfirmBody: "The stock for this item will be restored. This can't be undone.",
      voidReasonLabel: "Reason (optional)",
      voidReasonPlaceholder: "e.g. Customer returned the item",
      voidConfirmButton: "Void Sale",
      status: "Status",
      statusAll: "All",
      statusConfirmed: "Confirmed",
      statusVoided: "Voided",
      statusPartial: "Partially Voided",
      paymentMedium: "Payment Medium",
      allPaymentMediums: "All Payment Mediums",
      search: "Search",
      refund: "Refund",
      refunded: "Refund recorded — stock updated.",
      refunding: "Refunding...",
      refundedNote: "{refunded} of {total} unit(s) refunded",
      refundConfirmTitle: "Refund this item",
      refundRemaining: "{remaining} of {total} unit(s) remain refundable",
      refundQuantity: "Quantity",
      refundAmountOverride: "Amount (optional)",
      refundMethod: "Refund Method",
      methodCash: "Cash",
      methodCard: "Card",
      methodStoreCredit: "Store Credit",
      refundCondition: "Item Condition",
      conditionResellable: "Resellable",
      conditionDamaged: "Damaged",
      refundReasonLabel: "Reason (required)",
      refundReasonPlaceholder: "e.g. Customer returned the item",
      refundConfirmButton: "Confirm Refund",
      refundSlipTitle: "Refund Slip",
      refundNo: "Refund No",
      refundedAmount: "REFUNDED",
      refundCustomerOptionalHint: "Optional — only for your own tracking. Redemption works by the voucher code below, not by who's tagged here.",
      voucherCodeHint: "Voucher code {code} — present this to redeem, any future visit.",
    },
    storeCredit: {
      title: "Store Credit",
      totalOutstanding: "Total Outstanding",
      code: "Code",
      taggedCustomer: "Tagged Customer",
      notTagged: "—",
      issued: "Issued",
      balance: "Balance",
      empty: "No store credit vouchers outstanding.",
      date: "Date",
      amount: "Amount",
      appliedTo: "Applied To",
      loadingHistory: "Loading history...",
      noRedemptions: "Not redeemed yet.",
    },
    paymentMediums: {
      title: "Payment Mediums",
      pendingSectionTitle: "Pending Bank Transfers",
      empty: "No pending bank-transfer payments.",
      reference: "Reference",
      amount: "Amount",
      created: "Created",
      status: "Status",
      statusAwaiting: "Awaiting Payment",
      statusConfirmed: "Confirmed",
      statusCancelled: "Cancelled",
      statusAmbiguous: "Needs Review",
      markAsPaid: "Mark as Paid",
      cancelPayment: "Cancel",
      keepWaiting: "Keep Waiting",
      cancelReasonPrompt: "Reason for cancelling (optional):",
      confirmError: "Couldn't confirm — check stock/voucher balance and try again.",
      paidToast: "Marked as paid",
      cancelledToast: "Payment cancelled",
      lastError: "Last attempt failed",
      ambiguousExplanation: "A payment notification matched this and another pending payment by the same amount — pick which one actually got paid",
      forwarderStale: "Automatic payment detection may be down",
      forwarderLastSeen: "Phone last checked in",
    },
    shifts: {
      title: "Shifts",
      openShift: "Open Shift",
      openingFloat: "Opening Float",
      openingFloatHint: "Cash you're starting this shift with",
      openShiftSubmit: "Open Shift",
      openShiftSuccess: "Shift opened",
      currentShift: "Current Shift",
      openedAt: "Opened",
      openedBy: "Opened by",
      recordCashMovement: "Record Cash Movement",
      cashMovementAmount: "Amount",
      cashMovementAmountHint: "Positive if adding cash to the drawer, negative if taking cash out",
      cashMovementReason: "Reason",
      cashMovementContact: "Related Contact (optional)",
      cashMovementSubmit: "Record",
      cashMovementSuccess: "Cash movement recorded",
      closeShift: "Close Shift",
      expectedCash: "Expected Cash",
      countedCash: "Counted Cash",
      countedCashHint: "Count the drawer and enter the total",
      closeShiftNotes: "Notes (optional)",
      closeShiftSubmit: "Close Shift",
      closeShiftSuccess: "Shift closed",
      variance: "Variance",
      varianceMatch: "Matches",
      varianceShort: "Short",
      varianceOver: "Over",
      noOpenShift: "No shift open",
      history: "Shift History",
      noHistory: "No shifts yet.",
      status: "Status",
      statusOpen: "Open",
      statusClosed: "Closed",
      viewDetail: "View",
      alreadyHaveOpenShift: "You already have an open shift — close it before opening another.",
      noOpenShiftBanner: "No shift open — open one to start selling.",
      statusNeedsReview: "Needs Review",
      reconcile: "Reconcile",
      reconcileSubmit: "Save Count",
      reconcileSuccess: "Shift reconciled",
      autoClosedExplanation:
        "This shift was closed automatically because it was left open with no activity — nobody counted the drawer yet. Count it now and enter the real total.",
      closedAt: "Closed",
      filterAllStatuses: "All Statuses",
      filterAllUsers: "All Staff",
      filterOnlyVariance: "Only shifts with a variance",
      filterMinVariance: "Min variance",
      filterMaxVariance: "Max variance",
      loadingDetail: "Loading shift details...",
      detailSales: "Sales",
      detailRefunds: "Refunds",
      detailCashMovements: "Cash Movements",
      detailEmpty: "None",
    },
    report: {
      title: "Sales Report",
      loading: "Loading data...",
      period: "Period",
      filterLabel: "Filter",
      generatedOn: "Generated on",
      allProducts: "All Products",
      profitableProducts: "Profitable Products",
      lossProducts: "Loss-Making Products",
      paymentMedium: "Payment Medium",
      allPaymentMediums: "All Payment Mediums",
      paymentMediumBreakdown: "By Payment Medium",
      unrecordedMedium: "Unrecorded",
      shrinkageTitle: "Shrinkage",
      shrinkageTooltip:
        "Stock that left your inventory without a sale — damage, expiry, theft, or a physical count correction. Recorded via Inventory's Adjust Stock action, not through a checkout.",
      shrinkageUnitsLost: "Units Lost",
      shrinkageCostImpact: "Cost Impact",
      shrinkageByReason: "By Reason",
      shrinkageByProduct: "By Product",
      shrinkageEmpty: "No stock adjustments recorded in this period.",
      viewDetail: "View Detail",
      presetToday: "Today",
      presetYesterday: "Yesterday",
      presetLast7: "Last 7 days",
      presetThisMonth: "This month",
      presetLastMonth: "Last month",
      printReport: "Print report",
      netSales: "Net sales",
      grossProfit: "Gross profit",
      marginOf: "{n}% margin",
      transactions: "Transactions",
      averageSale: "Average sale",
      itemsSold: "Items sold",
      refunds: "Refunds",
      costOfGoods: "Cost of goods",
      voided: "Voided",
      vsPrevious: "vs previous period",
      vsPreviousNote: "Percentages compare each figure with the previous period of the same length.",
      noPrevious: "No comparison: there were no sales in the previous period of the same length.",
      localHistory: "Offline: showing only the sales kept on this register, from {date}. Older sales appear once it's connected to the internet.",
      localHistoryEmpty: "Offline: showing only the sales kept on this register. Older sales appear once it's connected to the internet.",
      localHistorySending: "This register is still sending its latest sales, so only the sales kept on it are shown. Refresh in a moment for the full history.",
      grossDetail: "Before refunds {amount}",
      refundsDetail: "{count} refunds · {items} items",
      voidsDetail: "{n} sales voided",
      itemsReturned: "{n} returned",
      perReceipt: "per receipt",
      salesByCategory: "Sales by category",
      salesByCashier: "Sales by cashier",
      category: "Category",
      cashier: "Cashier",
      share: "Share",
      sales: "Sales",
      unassigned: "Unassigned",
      productPerformance: "Product performance",
      searchProducts: "Search products",
      allCategories: "All categories",
      product: "Product",
      sold: "Sold",
      returned: "Returned",
      avgPrice: "Avg price",
      cost: "Cost",
      profit: "Profit",
      margin: "Margin",
      total: "Total",
      noSalesInPeriod: "No sales in this period.",
      showing: "{from}–{to} of {total} products",
      trendTab: "Trend",
      hoursTab: "Hours",
      daysTab: "Days",
      salesTrend: "Net sales & profit",
      trendHint_day: "Per day. Refunds count on the day they were given.",
      trendHint_week: "Per week (from Monday). Refunds count in the week they were given.",
      trendHint_month: "Per month. Refunds count in the month they were given.",
      busiestHours: "Busiest hours",
      busiestHoursHint: "Net sales in each hour of the day, all days in the period added together.",
      busiestDays: "Busiest days of the week",
      busiestDaysHint: "Net sales on each weekday, all weeks in the period added together.",
    },
    notices: {
      dueSoon: "Your subscription is due on {date} ({days} days left). Please renew in time so the app keeps working.",
      dueToday: "Your subscription is due today. Please renew so the app keeps working.",
      overdue: "Your subscription was due on {date} and is {days} days overdue. Please pay your provider to avoid interruption.",
      dismiss: "Dismiss",
    },
    notFound: {
      title: "Page not found",
      desc: "The page you're looking for doesn't exist or may have been moved.",
      backHome: "Back to Home",
    },
  },
  ur: {
    nav: {
      register: "کاؤنٹر",
      inventory: "انوینٹری",
      stockAdjustments: "اسٹاک ایڈجسٹمنٹس",
      salesHistory: "سیلز ہسٹری",
      creditDebit: "کریڈٹ / ڈیبٹ",
      contacts: "رابطے",
      storeCredit: "اسٹور کریڈٹ",
      paymentMediums: "ادائیگی کے ذرائع",
      shifts: "شفٹس",
      report: "سیلز رپورٹ",
      company: "کمپنی",
      settings: "ترتیبات",
      devices: "ڈیوائسز",
      support: "سپورٹ",
    },
    update: {
      codeReady: "ورژن {version} تیار ہے۔ استعمال کرنے کے لیے ایپ دوبارہ شروع کریں۔",
      appReady: "ایپ کا نیا ورژن ({version}) انسٹال کے لیے تیار ہے۔",
      restart: "ابھی دوبارہ شروع کریں",
      install: "انسٹال کریں",
      later: "بعد میں",
      allowInstall: "کھلنے والی اسکرین پر POS سے ایپس انسٹال کرنے کی اجازت دیں، پھر دوبارہ انسٹال دبائیں۔",
      installFailed: "اپ ڈیٹ انسٹال کے لیے نہیں کھل سکی۔",
      check: "اپ ڈیٹ چیک کریں",
      version: "ورژن {version}",
      upToDate: "آپ کے پاس تازہ ترین ورژن ہے۔",
      found: "ورژن {version} مل گیا۔",
      downloading: "ورژن {version} ڈاؤن لوڈ ہو رہا ہے…",
      checkFailed: "اپ ڈیٹ چیک نہیں ہو سکی: {message}",
    },
    support: {
      pageTitle: "سپورٹ",
      introOwner: "کوئی مسئلہ بتائیں یا نئی سہولت کی درخواست کریں، پھر اس کا اسٹیٹس یہاں دیکھیں۔ آپ کو اپنی دکان کے تمام ٹکٹ نظر آئیں گے۔",
      introStaff: "کوئی مسئلہ بتائیں یا نئی سہولت کی درخواست کریں، پھر اس کا اسٹیٹس یہاں دیکھیں۔ آپ کو اپنے کھولے ہوئے ٹکٹ نظر آئیں گے۔",
      newTicket: "نیا ٹکٹ",
      reportProblem: "مسئلہ بتائیں / تجویز دیں",
      kindBug: "خرابی",
      kindFeature: "نئی سہولت",
      kindBugHint: "کوئی چیز صحیح کام نہیں کر رہی",
      kindFeatureHint: "کوئی نئی چیز جو آپ ایپ میں چاہتے ہیں",
      title: "عنوان",
      titleBugPlaceholder: "مثلاً رسید دو بار پرنٹ ہوتی ہے",
      titleFeaturePlaceholder: "مثلاً رسید واٹس ایپ پر بھیجیں",
      description: "تفصیل",
      bodyBugPlaceholder: "کیا ہوا، آپ کیا توقع کر رہے تھے، اور دوبارہ ایسا کرنے کے مراحل۔",
      bodyFeaturePlaceholder: "آپ کیا چاہتے ہیں، اور اس سے دکان کو کیا فائدہ ہوگا۔",
      pictures: "اسکرین شاٹ (اختیاری)",
      addPicture: "تصویر شامل کریں",
      removePicture: "تصویر ہٹائیں",
      picturesMax: "ایک پیغام میں زیادہ سے زیادہ {n} تصویریں۔",
      pictureTooBig: "یہ تصویر چھوٹی کرنے کے بعد بھی بہت بڑی ہے۔",
      notAPicture: "یہ فائل تصویر نہیں ہے۔",
      submit: "ٹکٹ بھیجیں",
      sending: "بھیجا جا رہا ہے...",
      created: "ٹکٹ بھیج دیا گیا۔ اس پر کام کے ساتھ اس کا اسٹیٹس یہاں نظر آئے گا۔",
      includeLogs: "تشخیصی لاگ بھیجیں (تجویز کردہ)",
      includeLogsHint: "پچھلے {minutes} منٹ میں ایپ نے جو ریکارڈ کیا ({n} اندراجات): کھولے گئے صفحات، درخواستیں اور ان کا نتیجہ، غلطیاں اور دکھائے گئے پیغامات۔ کوئی پاس ورڈ یا سیل کی تفصیل نہیں۔",
      empty: "ابھی یہاں کوئی ٹکٹ نہیں۔",
      filter_active: "کھلے",
      filter_closed: "بند",
      filter_all: "سب",
      newReply: "اسٹیٹس بدلا",
      back: "تمام ٹکٹ",
      report: "کیا بتایا گیا",
      statusHistory: "اسٹیٹس کی تاریخ",
      historyOpened: "{name} نے کھولا",
      wontFixNote: "سپورٹ ٹیم نے اس پر آگے کام نہ کرنے کا فیصلہ کیا۔",
      diagnosticsSent: "اس ٹکٹ کے ساتھ تشخیصی لاگ بھیجے گئے۔",
      diagnosticsNotSent: "اس ٹکٹ کے ساتھ تشخیصی لاگ نہیں بھیجے گئے۔",
      status_open: "کھلا",
      status_in_review: "زیرِ غور",
      status_planned: "منصوبے میں",
      status_in_progress: "کام جاری",
      status_resolved: "حل ہو گیا",
      status_wont_fix: "نہیں ہوگا",
      badge: "آپ کے سپورٹ ٹکٹوں کا اسٹیٹس بدلا",
      problemPromptTitle: "کچھ غلط ہو گیا",
      problemPromptBody: "کیا اسے رپورٹ کریں تاکہ ٹھیک ہو سکے؟ ایپ نے ابھی جو ریکارڈ کیا وہ رپورٹ کے ساتھ جائے گا۔",
      problemReport: "رپورٹ کریں",
      problemNotNow: "ابھی نہیں",
      problemDismiss: "بند کریں",
      problemTitle: "ایپ نے غلطی دکھائی",
      problemNotice: "ابھی والی غلطی ریکارڈ ہو گئی ہے اور تشخیصی لاگ کے ساتھ جائے گی۔ بتائیں کہ اس وقت آپ کیا کر رہے تھے۔",
      problemBodyPlaceholder: "جب یہ ہوا تو آپ کیا کر رہے تھے؟ مثلاً میں نے 3 چیزوں والی سیل پر ادائیگی دبائی۔",
      crashTitle: "اس صفحے پر کچھ غلط ہو گیا",
      crashBody: "جاری رکھنے کے لیے دوبارہ لوڈ کریں۔ اگر بار بار ہو تو رپورٹ کریں — جو ہوا وہ رپورٹ کے ساتھ جائے گا۔",
      crashReload: "دوبارہ لوڈ کریں",
      crashReport: "مسئلہ رپورٹ کریں",
    },
    common: {
      add: "شامل کریں",
      edit: "ترمیم",
      delete: "حذف کریں",
      update: "اپڈیٹ",
      cancel: "منسوخ",
      save: "محفوظ کریں",
      saving: "محفوظ ہو رہا ہے...",
      submit: "جمع کریں",
      loading: "لوڈ ہو رہا ہے…",
    },
    pos: {
      addProduct: "پروڈکٹ شامل کریں",
      addCategory: "کیٹیگری شامل کریں",
      update: "اپڈیٹ",
    },
    register: {
      title: "کاؤنٹر",
      all: "سب",
      searchPlaceholder: "پروڈکٹ تلاش کریں یا لاٹ کوڈ اسکین کریں",
      manage: "انتظام",
      editProducts: "پروڈکٹس میں ترمیم",
      shiftOpen: "شفٹ کھلی ہے",
      noShift: "کوئی شفٹ نہیں کھلی",
      noShiftWarning: "کوئی شفٹ نہیں کھلی — شفٹ کھولے بغیر یہ سیل مکمل نہیں ہو سکتی۔",
      shiftOpenLong: "شفٹ {since} سے کھلی ہے · گنیں اور بند کریں",
      left: "{n} باقی",
      outOfStock: "اسٹاک ختم",
      lots: "لاٹس",
      setPrice: "قیمت درج کریں",
      noResults: "“{q}” سے کوئی پروڈکٹ نہیں ملی۔",
      lotMatch: "لاٹ {code}",
      gridView: "گرڈ ویو",
      listView: "فہرست ویو",
      emptyCatalog: "ابھی کوئی پروڈکٹ نہیں۔",
      onlyNLeft: "صرف {n} اسٹاک میں ہیں۔",
      currentSale: "موجودہ سیل",
      itemsCount: "{n} آئٹمز",
      oneItem: "1 آئٹم",
      pay: "ادائیگی",
      hold: "روکیں",
      held: "رکی ہوئی",
      heldTitle: "رکی ہوئی سیلز",
      heldEmpty: "کوئی رکی ہوئی سیل نہیں۔",
      resume: "دوبارہ شروع کریں",
      discard: "ختم کریں",
      clearSale: "صاف کریں",
      clearConfirm: "اس سیل سے تمام آئٹمز ہٹا دیں؟",
      heldSaved: "سیل روک دی گئی — 'رکی ہوئی' سے کبھی بھی دوبارہ شروع کریں۔",
      heldFull: "آپ ایک وقت میں زیادہ سے زیادہ {n} سیلز روک سکتے ہیں۔",
      belowCost: "خرید قیمت سے کم",
      lastSold: "پچھلی فروخت",
      lastShort: "قیمت درج کریں · پچھلی {amount}",
      alreadyInSale: "یہ اس سیل میں پہلے سے ہے۔ ایک اور اسی قیمت پر شامل کریں یا نئی قیمت پر؟",
      samePrice: "وہی قیمت · {amount}",
      newPrice: "نئی قیمت",
      margin: "منافع",
      each: "فی عدد",
      editPrice: "قیمت میں ترمیم",
      chooseLot: "لاٹ منتخب کریں",
      completeSale: "سیل مکمل کریں",
      exact: "پوری رقم",
      received: "موصول",
      shortcuts: "F2 تلاش · F4 ادائیگی · Esc بند",
    },
    sell: {
      inStock: "اسٹاک میں",
      costPrice: "خرید قیمت",
      quantity: "مقدار",
      sellingPrice: "فروخت قیمت",
      quantityError: "1 سے {max} کے درمیان مقدار درج کریں۔",
      cancel: "منسوخ",
      addToCart: "کارٹ میں شامل کریں",
    },
    cart: {
      empty: "آپ کا کارٹ خالی ہے۔",
      emptyHint: "سیل شروع کرنے کے لیے کسی پروڈکٹ پر ٹیپ کریں یا لاٹ کوڈ اسکین کریں۔",
      subtotal: "ذیلی مجموعہ",
      remove: "ہٹائیں",
    },
    payment: {
      title: "ادائیگی",
      amountDue: "واجب الادا رقم",
      cash: "نقد",
      card: "کارڈ",
      amountReceived: "موصولہ رقم",
      amountReceivedPlaceholder: "موصولہ نقد رقم درج کریں",
      changeDue: "واپسی رقم",
      amountShort: "رقم کم ہے",
      cancel: "منسوخ",
      confirmSale: "سیل کی تصدیق کریں",
      processing: "کارروائی جاری ہے...",
      haveVoucherCode: "کیا اسٹور کریڈٹ واؤچر کوڈ ہے؟",
      hideStoreCredit: "واؤچر کوڈ چھپائیں",
      voucherCode: "واؤچر کوڈ",
      offlineVoucherTitle: "مالک کی منظوری چاہیے",
      offlineVoucherBody:
        "یہ رجسٹر آف لائن ہے، اس لیے یہ نہیں دیکھ سکتا کہ یہ واؤچر کسی دوسرے رجسٹر پر پہلے استعمال تو نہیں ہوا۔ مالک اپنا پاس ورڈ ڈال کر اجازت دے سکتا ہے۔",
      ownerPassword: "مالک کا پاس ورڈ",
      approve: "منظور کریں",
      invalidVoucherCode: "غلط یا نامعلوم واؤچر کوڈ۔",
      loadingBalance: "بیلنس لوڈ ہو رہا ہے...",
      availableCredit: "دستیاب کریڈٹ",
      amountToRedeem: "استعمال کرنے کی رقم",
      storeCreditApplied: "اسٹور کریڈٹ لاگو ہوا",
      fullyCoveredByCredit: "پوری رقم اسٹور کریڈٹ سے ادا ہو گئی — کچھ اور واجب نہیں۔",
      bankTransfer: "بینک ٹرانسفر",
      bankTransferUnavailableOffline: "بینک ٹرانسفر کے لیے انٹرنیٹ ضروری ہے — آف لائن دستیاب نہیں",
      scanToPay: "ادائیگی کے لیے بینکنگ ایپ سے اسکین کریں",
      reference: "حوالہ نمبر",
      waitingForConfirmation: "ادائیگی کا انتظار ہے — رقم ملنے پر 'ادا شدہ' نشان زد کریں",
      markAsPaid: "ادا شدہ نشان زد کریں",
      close: "بند کریں",
    },
    receipt: {
      title: "رسید",
      receiptTitle: "رسید",
      receiptNo: "رسید نمبر",
      noItems: "کوئی آئٹم نہیں",
      total: "کل رقم",
      storeCreditApplied: "اسٹور کریڈٹ لاگو ہوا",
      amountPaid: "ادا شدہ رقم",
      thankYou: "خریداری کا شکریہ!",
      done: "مکمل",
      print: "پرنٹ",
      printing: "پرنٹ ہو رہا ہے...",
      printError: "رسید پرنٹ نہیں ہو سکی۔",
    },
    time: {
      never: "کبھی نہیں",
      justNow: "ابھی",
      minutesAgo: "{n} منٹ پہلے",
      hoursAgo: "{n} گھنٹے پہلے",
      dayAgo: "1 دن پہلے",
      daysAgo: "{n} دن پہلے",
    },
    devices: {
      title: "ڈیوائسز",
      intro:
        "وہ رجسٹر جو انٹرنیٹ کے بغیر بھی سیل کرتے ہیں: پی سی پر ونڈوز ایپ، فون پر اینڈرائیڈ ایپ۔ انٹرنیٹ ملتے ہی ہر ایک اپنی سیلز یہاں بھیج دیتا ہے۔",
      howToAdd: "نئی ڈیوائس جوڑنے کے لیے پی سی یا فون پر POS ایپ انسٹال کریں اور وہاں اپنے مالک کے اکاؤنٹ سے سائن ان کریں۔",
      limit: "{limit} میں سے {used} ڈیوائسز استعمال میں",
      none: "ابھی کوئی ڈیوائس نہیں۔",
      lastSync: "آخری سنک",
      waiting: "{n} بھیجنا باقی",
      waitingAsOf: "آخری رابطے کے مطابق",
      registeredBy: "{name} نے {when} جوڑی",
      clockOff: "اس ڈیوائس کی گھڑی {minutes} منٹ آگے پیچھے ہے — اس کی تاریخ اور وقت درست کریں۔",
      retire: "ریٹائر",
      block: "بلاک",
      retireConfirm:
        "{name} کو ریٹائر کریں؟ یہ فوراً سنک کرنا بند کر دے گی۔ یہ تب کریں جب ڈیوائس بدل دی گئی ہو؛ جو اس نے ابھی نہیں بھیجا وہ اسی پر رہے گا۔",
      blockConfirm: "{name} کو بلاک کریں؟ یہ تب کریں جب ڈیوائس گم یا چوری ہو جائے: یہ فوراً کٹ جائے گی اور اس کی بھیجی ہر چیز رد ہو گی۔",
      retired: "{name} ریٹائر ہو گئی۔",
      blocked: "{name} بلاک ہو گئی۔",
      state: {
        in_sync: "سنک میں",
        behind: "پیچھے",
        offline_long: "کافی دیر سے آف لائن",
        retired: "ریٹائرڈ",
        blocked: "بلاکڈ",
      },
      issuesTitle: "سنک کے مسائل",
      issuesIntro: "ڈیوائس کی وہ تبدیلیاں جو یہاں محفوظ نہ ہو سکیں۔ ہر ایک ایپ میں دیکھیں، پھر اسے نمٹا ہوا نشان لگائیں۔",
      noIssues: "سنک کا کوئی مسئلہ نہیں۔",
      resolve: "نمٹا دیا",
    },
    deviceSync: {
      synced: "سنک {when}",
      syncing: "سنک ہو رہا ہے…",
      offline: "آف لائن",
      waiting: "{n} باقی",
      syncNow: "ابھی سنک کریں",
      warn: "{days} دن سے انٹرنیٹ نہیں — اس رجسٹر کو جوڑیں تاکہ اس کی سیلز مالک تک پہنچیں۔",
      blocked: "یہ رجسٹر {days} دن سے آف لائن ہے۔ سنک کے لیے اسے انٹرنیٹ سے جوڑیں، پھر سیل جاری رکھیں۔",
      revoked: "مالک نے یہ رجسٹر ریٹائر یا بلاک کر دیا ہے اور یہ اب سنک نہیں کرتا۔",
    },
    deviceSetup: {
      title: "یہ ڈیوائس سیٹ اپ کریں",
      intro: "دکان کے مالک کے اکاؤنٹ سے سائن ان کریں۔ یہ ڈیوائس دکان کا ڈیٹا ڈاؤن لوڈ کرے گی اور پھر انٹرنیٹ کے بغیر بھی چلے گی۔",
      ownerUsername: "مالک کا یوزر نیم",
      deviceName: "اس ڈیوائس کا نام",
      deviceNameHint: "مثلاً کاؤنٹر پی سی یا علی کا فون — مالک کی ڈیوائسز کی فہرست میں نظر آئے گا۔",
      server: "سرور کا پتہ",
      changeServer: "سرور کا پتہ بدلیں",
      submit: "ڈیوائس سیٹ اپ کریں",
      needsInternet: "سیٹ اپ کے لیے ایک بار انٹرنیٹ چاہیے۔",
      done: "یہ ڈیوائس {shop} کے لیے تیار ہے۔ شروع کرنے کے لیے سائن ان کریں۔",
      step: {
        signing_in: "سائن ان ہو رہا ہے",
        registering: "ڈیوائس رجسٹر ہو رہی ہے",
        downloading: "دکان کا ڈیٹا ڈاؤن لوڈ ہو رہا ہے",
        saving: "اس ڈیوائس پر محفوظ ہو رہا ہے",
      },
    },
    auth: {
      title: "لاگ ان کریں",
      deviceShop: "یہ رجسٹر {shop} کا ہے۔ یہاں صرف اسی دکان کا عملہ سائن ان کر سکتا ہے۔",
      username: "یوزر نیم",
      password: "پاس ورڈ",
      login: "لاگ ان",
      loggingIn: "لاگ ان ہو رہا ہے...",
      loginError: "کچھ غلط ہو گیا۔ دوبارہ کوشش کریں۔",
      logout: "لاگ آؤٹ",
      roleOwner: "مالک",
      roleCashier: "کیشیئر",
      usersTitle: "یوزرز",
      usersDesc: "اس دکان کے مالک اور کیشیئر اکاؤنٹس۔ اکاؤنٹ غیر فعال کرنے سے صرف لاگ ان بند ہوگا، سیلز ہسٹری محفوظ رہے گی۔",
      addUser: "یوزر شامل کریں",
      displayName: "نام",
      role: "کردار",
      userAdded: "یوزر شامل ہو گیا",
      you: "آپ",
      active: "فعال",
      inactive: "غیر فعال",
      cantDeactivateSelf: "آپ اپنا اکاؤنٹ غیر فعال نہیں کر سکتے",
      editUser: "یوزر میں ترمیم کریں",
      userUpdated: "یوزر اپڈیٹ ہو گیا",
      newPasswordOptional: "نیا پاس ورڈ (اختیاری)",
      newPasswordPlaceholder: "موجودہ پاس ورڈ رکھنے کے لیے خالی چھوڑیں",
      newPasswordHint: "صرف اسی صورت میں بھریں اگر پاس ورڈ تبدیل کرنا ہے۔",
      userLimitReached: "یوزر کی حد پوری ہو گئی ({max}) — مزید یوزرز شامل کرنے کے لیے اپنے پرووائیڈر سے حد بڑھوائیں۔",
      usersOfLimit: "{max} میں سے {count} یوزرز",
      forgotPassword: "پاس ورڈ بھول گئے؟",
      forgotPasswordTitle: "پاس ورڈ ری سیٹ کی درخواست",
      forgotPasswordDesc:
        "اپنا یوزر نیم اور کم از کم CNIC یا فون نمبر درج کریں تاکہ ایڈمن آپ کی شناخت کی تصدیق کر کے پاس ورڈ ری سیٹ کر سکے۔",
      forgotPasswordCnic: "CNIC (اختیاری)",
      forgotPasswordPhone: "فون نمبر (اختیاری)",
      forgotPasswordIdentityHint: "CNIC یا فون نمبر میں سے کم از کم ایک ضرور درج کریں۔",
      forgotPasswordSubmit: "درخواست جمع کروائیں",
      forgotPasswordSubmitting: "جمع ہو رہی ہے...",
      forgotPasswordSuccess: "اگر اکاؤنٹ مل گیا تو آپ کی درخواست ایڈمن کو بھیج دی گئی ہے۔",
      setNewPasswordTitle: "نیا پاس ورڈ سیٹ کریں",
      setNewPasswordDesc: "آپ کا پاس ورڈ ایڈمن نے ری سیٹ کیا ہے۔ جاری رکھنے کے لیے نیا پاس ورڈ منتخب کریں۔",
      newPassword: "نیا پاس ورڈ",
      confirmNewPassword: "نئے پاس ورڈ کی تصدیق",
      newPasswordMismatch: "نیا پاس ورڈ اور تصدیق مماثل نہیں ہیں۔",
      changePassword: "پاس ورڈ تبدیل کریں",
      currentPassword: "موجودہ پاس ورڈ",
      passwordMinLength: "کم از کم 8 حروف",
      passwordChanged: "پاس ورڈ تبدیل ہو گیا۔ اب سے ہر ڈیوائس پر نیا پاس ورڈ استعمال کریں۔",
      setNewPasswordSubmit: "پاس ورڈ سیٹ کریں",
      setNewPasswordSubmitting: "محفوظ ہو رہا ہے...",
    },
    settings: {
      title: "ترتیبات",
      loading: "ترتیبات لوڈ ہو رہی ہیں...",
      lowStockTitle: "کم اسٹاک حد",
      lowStockDesc:
        "اس مقدار سے نیچے آنے پر پروڈکٹ کو \"کم اسٹاک\" کے طور پر نشان زد کیا جائے گا (انوینٹری صفحہ اور فروخت کے وقت دونوں جگہ)۔",
      batchNote: "لاٹ/بیچ ٹریکنگ اب ہر پروڈکٹ کے لیے \"پروڈکٹ شامل کریں\" میں الگ سے آن کی جاتی ہے۔",
      languageTitle: "زبان",
      languageDesc: "پورے ایپ کی زبان یہاں سے تبدیل کی جا سکتی ہے۔",
      textSizeTitle: "تحریر کا سائز",
      textSizeDesc: "اس ڈیوائس پر تحریر اور بٹن کتنے بڑے ہوں۔ L معیاری سائز ہے؛ فون کی اسکرین پر زیادہ چیزیں دکھانے کے لیے چھوٹا سائز چنیں۔",
      textSize_xs: "بہت چھوٹا",
      textSize_sm: "چھوٹا",
      textSize_md: "درمیانہ",
      textSize_lg: "بڑا (معیاری)",
      textSize_xl: "بہت بڑا",
      english: "English",
      urdu: "اردو",
      receiptPrinterTitle: "رسید پرنٹر",
      testPrint: "ٹیسٹ پرنٹ",
      connectBluetooth: "بلوٹوتھ پرنٹر منسلک کریں",
      connectUsb: "یو ایس بی پرنٹر منسلک کریں",
      disconnect: "منقطع کریں",
      forwarderTitle: "بینک SMS فارورڈر",
      forwarderDesc:
        "اینڈرائیڈ فارورڈر ایپ اس دکان کے بینک اور JazzCash ادائیگی کے پیغامات یہاں بھیجتی ہے، تاکہ بینک ٹرانسفر والی سیلز خود بخود کنفرم ہو جائیں۔ ہر دکان کا اپنا سیکرٹ ہوتا ہے — اسے فون ایپ کے Secret خانے میں لکھیں۔",
      forwarderConfiguredOn: "سیکرٹ بنایا گیا",
      forwarderNotConfigured: "ابھی کوئی سیکرٹ نہیں — سیکرٹ بنانے تک فون ایپ کنیکٹ نہیں ہو سکتی۔",
      forwarderGenerate: "سیکرٹ بنائیں",
      forwarderRegenerate: "نیا سیکرٹ بنائیں",
      forwarderRegenerateConfirm:
        "جب تک آپ فون ایپ میں نیا سیکرٹ نہیں لکھتے، ادائیگیاں خود بخود کنفرم نہیں ہوں گی۔ جاری رکھیں؟",
      forwarderSecretOnce: "اسے ابھی کاپی کر کے فون ایپ میں لکھ دیں — یہ دوبارہ نہیں دکھایا جائے گا۔",
      forwarderGenerated: "نیا فارورڈر سیکرٹ بن گیا",
      copy: "کاپی کریں",
      copied: "کاپی ہو گیا",
      receiptTermsTitle: "رسید کی شرائط و ضوابط",
      receiptTermsDesc: "یہ متن رسید کے بالکل نیچے جیسا لکھا جائے ویسا ہی چھپے گا۔ ہر لائن الگ سے پرنٹ ہوگی۔",
      refundWindowTitle: "ریفنڈ ونڈو (دن)",
      refundWindowDesc: "اتنے دنوں سے پرانی سیل ریفنڈ نہیں ہو سکے گی۔ کوئی حد نہ رکھنے کے لیے خالی چھوڑ دیں۔",
      refundWindowUnlimited: "کوئی حد نہیں",
      refundWindowUpdated: "ریفنڈ ونڈو اپڈیٹ ہو گئی",
      timezoneTitle: "ٹائم زون",
      timezoneDesc: "ایپ میں ہر جگہ دکھایا یا پرنٹ ہونے والا وقت — رسیدیں، سیلز ہسٹری، رپورٹس — اسی ٹائم زون میں ہوگا، ہر ڈیوائس پر ایک جیسا، ہر دیکھنے والے کا اپنا الگ نہیں۔",
      timezoneAuto: "خودکار",
      timezoneUpdated: "ٹائم زون اپڈیٹ ہو گیا",
    },
    company: {
      title: "کمپنی",
      detailsTitle: "کمپنی کی تفصیلات",
      detailsDesc: "یہ تفصیلات رسید/سلپ پر پرنٹ ہوں گی — نام اور لوگو ہیڈر میں، باقی نیچے۔",
      companyName: "کمپنی کا نام",
      ntnNumber: "این ٹی این نمبر",
      phone: "فون",
      address: "پتہ",
      logo: "لوگو",
      optional: "اختیاری",
      upload: "اپ لوڈ",
      change: "تبدیل کریں",
      saveChanges: "تبدیلیاں محفوظ کریں",
      loading: "کمپنی کی تفصیلات لوڈ ہو رہی ہیں...",
      bankDetailsTitle: "بینک اکاؤنٹ (کیو آر ادائیگیوں کے لیے)",
      bankDetailsDesc: "یہ اس کیو آر کوڈ پر ظاہر ہوں گی جسے کسٹمر بینک ٹرانسفر سے ادائیگی کے لیے اسکین کریں گے۔",
      bankName: "بینک کا نام",
      selectBank: "بینک منتخب کریں",
      otherBank: "دیگر / فہرست میں نہیں",
      otherBankPlaceholder: "بینک کا نام درج کریں",
      bankAccountTitle: "اکاؤنٹ ٹائٹل",
      bankAccountNumber: "اکاؤنٹ نمبر",
      bankIban: "آئی بی اے این",
      bankIbanHint: "لازمی — یہی اصل میں کیو آر کوڈ میں جاتا ہے (پاکستان کا Raast نظام اکاؤنٹ نمبر نہیں، IBAN سے ادائیگی کرتا ہے)۔ جاز کیش/ایزی پیسہ جیسے والٹس کا بھی IBAN ہوتا ہے، چاہے ایپ میں سیدھا نظر نہ آئے — اکاؤنٹ ڈیٹیلز میں دیکھیں یا ہیلپ لائن پر کال کریں۔",
    },
    contacts: {
      title: "رابطے",
      addContact: "رابطہ شامل کریں",
      all: "تمام",
      vendors: "وینڈرز",
      customers: "کسٹمرز",
      empty: "ابھی کوئی رابطہ نہیں۔ کسٹمر یا وینڈر شامل کریں۔",
      noDetails: "کوئی رابطہ کی تفصیلات نہیں۔",
      vendor: "وینڈر",
      customer: "کسٹمر",
    },
    creditDebit: {
      title: "کریڈٹ / ڈیبٹ",
      pendingPayables: "زیر التواء قابل ادا",
      pendingPayablesInfo: "وہ کل رقم جو آپ نے فی الحال تمام سپلائرز کو ملا کر ادا کرنی ہے۔",
      pendingReceivables: "زیر التواء قابل وصول",
      pendingReceivablesInfo: "وہ کل رقم جو تمام کسٹمرز پر فی الحال آپ کی واجب الادا ہے۔",
      netPosition: "خالص پوزیشن",
      netPositionInfo:
        "قابل وصول مائنس قابل ادا۔ اگر آج سب کا حساب برابر ہو جائے تو یہ بتاتا ہے کہ آپ کو خالص کتنا ملے گا (مثبت) یا دینا ہوگا (منفی)۔",
      totalOutstanding: "کل زیر التواء رقم",
      totalOutstandingInfo: "قابل ادا جمع قابل وصول — آپ کے پورے لیجر میں فی الحال کل زیر التواء رقم۔",
      payableTitle: "قابل ادا — آپ سپلائرز کے مقروض ہیں",
      receivableTitle: "قابل وصول — کسٹمرز آپ کے مقروض ہیں",
      addPayable: "قابل ادا شامل کریں",
      addReceivable: "قابل وصول شامل کریں",
      noPayables: "کوئی قابل ادا اندراج نہیں۔",
      noReceivables: "کوئی قابل وصول اندراج نہیں۔",
      supplier: "سپلائر",
      customer: "کسٹمر",
      due: "واجب الادا",
      paid: "ادا شدہ",
      pending: "زیر التواء",
      settle: "ادائیگی کریں",
      addCharge: "چارج شامل کریں",
      netOff: "نیٹ آف",
      netOffDone: "نیٹ آف درج ہو گیا!",
      netOffExplain: "یہ رقم دونوں طرف ادا شدہ کے طور پر درج ہو جائے گی — کوئی نقدی منتقل نہیں ہوگی۔",
      payableBalance: "قابل ادا",
      receivableBalance: "قابل وصول",
      amount: "رقم",
      date: "تاریخ",
      type: "قسم",
      note: "نوٹ",
      optional: "اختیاری",
      addEntry: "اندراج شامل کریں",
      saveChanges: "تبدیلیاں محفوظ کریں",
      selectCustomer: "کسٹمر منتخب کریں",
      selectVendor: "وینڈر منتخب کریں",
      entryAdded: "اندراج شامل ہو گیا!",
      paymentRecorded: "ادائیگی درج ہو گئی!",
      enterValidAmount: "0 سے زیادہ رقم درج کریں۔",
      history: "تاریخچہ",
      loadingHistory: "تاریخچہ لوڈ ہو رہا ہے...",
      noHistory: "ابھی کوئی اندراج نہیں۔",
      runningBalance: "بیلنس",
      chargeReceivable: "ادھار دیا",
      paymentReceivable: "وصولی",
      chargePayable: "ادھار خریدا",
      paymentPayable: "ادائیگی",
      adjustment: "ایڈجسٹمنٹ",
      entryUpdated: "اندراج اپڈیٹ ہو گیا!",
      entryDeleted: "اندراج حذف ہو گیا!",
      confirmDeleteEntry: "یہ اندراج حذف کریں؟ یہ واپس نہیں ہو سکتا۔",
      addChargeInfo:
        "نئی واجب الادا رقم درج کرتا ہے — یا تو آپ نے سپلائر کو مزید دینا ہے (قابل ادا)، یا کسٹمر نے آپ کو مزید دینا ہے (قابل وصول)۔",
      settleInfo: "ادائیگی درج کرتا ہے — مکمل یا جزوی — جو اس طرف واجب الادا رقم کم کر دیتی ہے۔",
      netOffInfo:
        "ایسے فریق کے لیے جس پر آپ کا حق بھی ہے اور جس کا آپ پر حق بھی ہے — یہ رقم دونوں طرف ادا شدہ کے طور پر درج کر دیتا ہے، بغیر اصل نقدی کی منتقلی کے۔",
    },
    inventory: {
      title: "انوینٹری",
      totalSkus: "کل SKUs",
      totalStockValue: "کل اسٹاک ویلیو",
      lowStock: "کم اسٹاک",
      outOfStock: "اسٹاک ختم",
      searchPlaceholder: "نام سے تلاش کریں...",
      allCategories: "تمام کیٹیگریز",
      allStatuses: "تمام حالتیں",
      inStock: "اسٹاک میں",
      product: "پروڈکٹ",
      category: "کیٹیگری",
      qty: "مقدار",
      price: "قیمت",
      value: "ویلیو",
      status: "حالت",
      noMatch: "ان فلٹرز سے کوئی پروڈکٹ میچ نہیں ہوتا۔",
      noLots: "کوئی لاٹ ریکارڈ نہیں۔",
      loadingLots: "لاٹس لوڈ ہو رہے ہیں...",
      lotCode: "لاٹ کوڈ",
      vendor: "وینڈر",
      remaining: "باقی",
      received: "موصول شدہ",
      adjustStock: "اسٹاک ایڈجسٹ کریں",
      adjustStockTitle: "اسٹاک ایڈجسٹ کریں",
      adjustStockLot: "لاٹ",
      adjustStockQuantityChange: "مقدار میں تبدیلی",
      adjustStockQuantityHint: "اضافے کے لیے مثبت نمبر (درستگی)، کمی کے لیے منفی نمبر (نقصان/کمی)",
      adjustStockReason: "وجہ",
      adjustStockReasonRestock: "دوبارہ اسٹاک (مزید مال آیا)",
      adjustStockReasonDamaged: "خراب",
      adjustStockReasonExpired: "میعاد ختم",
      adjustStockReasonTheft: "چوری",
      adjustStockReasonCountCorrection: "گنتی کی درستگی",
      adjustStockReasonOther: "دیگر",
      adjustStockNote: "نوٹ (اختیاری)",
      adjustStockSubmit: "ایڈجسٹمنٹ درج کریں",
      adjustStockSuccess: "اسٹاک ایڈجسٹمنٹ درج ہو گئی",
      restock: "اسٹاک بڑھائیں",
      restockTitle: "اسٹاک بڑھائیں",
      restockQuantity: "موصول شدہ تعداد",
      restockQuantityHint: "کتنے یونٹ آئے۔ یہ اسٹاک میں شامل ہوں گے اور اسٹاک ایڈجسٹمنٹ میں درج ہوں گے۔",
      restockSubmit: "اسٹاک میں شامل کریں",
      inStockNow: "اس وقت {qty} اسٹاک میں",
      stockAlerts: "اسٹاک الرٹس",
      stockAlertsEmpty: "تمام اشیاء کا اسٹاک ٹھیک ہے۔",
      stockAlertsHint: "اسٹاک بڑھانے کے لیے پروڈکٹ پر کلک کریں۔",
      unitsLeft: "{qty} باقی",
      openInventory: "انوینٹری کھولیں",
      quantityLockedHint: "مقدار یہاں تبدیل نہیں ہو سکتی — بڑھانے یا کم کرنے کے لیے وجہ کے ساتھ \"اسٹاک ایڈجسٹ کریں\" استعمال کریں۔",
    },
    stockAdjustments: {
      title: "اسٹاک ایڈجسٹمنٹس",
      allReasons: "تمام وجوہات",
      date: "تاریخ",
      quantityChange: "مقدار میں تبدیلی",
      adjustedBy: "کس نے ایڈجسٹ کیا",
      empty: "ابھی تک کوئی اسٹاک ایڈجسٹمنٹ درج نہیں ہوئی۔",
      note: "نوٹ",
      traceability: "ٹریسیبلٹی",
      lotReceivedAt: "لاٹ موصول ہوئی",
      lotReceivedBy: "کس نے قبول کیا",
      filteredByProduct: "پروڈکٹ کے مطابق فلٹر شدہ",
    },
    salesHistory: {
      title: "سیلز ہسٹری",
      startDate: "شروع کی تاریخ:",
      endDate: "آخری تاریخ:",
      category: "کیٹیگری:",
      allCategories: "تمام کیٹیگریز",
      clearFilter: "فلٹر ہٹائیں",
      dateTime: "تاریخ/وقت",
      receiptNo: "رسید نمبر",
      items: "اشیاء",
      total: "کل",
      loading: "لوڈ ہو رہا ہے...",
      empty: "کوئی پرانی سیل نہیں ملی۔",
      print: "پرنٹ",
      buyingPrice: "خرید قیمت",
      sellingPrice: "فروخت قیمت",
      lineTotal: "لائن کل",
      void: "منسوخ کریں",
      voidedBadge: "منسوخ شدہ",
      remoteSale: "پرانی سیل: اسے ویب ایپ سے منسوخ یا ریفنڈ کریں",
      voided: "سیل منسوخ ہو گئی — اسٹاک واپس آ گیا۔",
      voiding: "منسوخ ہو رہا ہے...",
      voidConfirmTitle: "یہ سیل منسوخ کریں؟",
      voidConfirmBody: "اس آئٹم کا اسٹاک واپس آ جائے گا۔ یہ واپس نہیں ہو سکتا۔",
      voidReasonLabel: "وجہ (اختیاری)",
      voidReasonPlaceholder: "مثلاً کسٹمر نے آئٹم واپس کر دیا",
      voidConfirmButton: "سیل منسوخ کریں",
      status: "حالت",
      statusAll: "تمام",
      statusConfirmed: "تصدیق شدہ",
      statusVoided: "منسوخ شدہ",
      statusPartial: "جزوی طور پر منسوخ",
      paymentMedium: "ادائیگی کا ذریعہ",
      allPaymentMediums: "تمام ادائیگی کے ذرائع",
      search: "تلاش کریں",
      refund: "ریفنڈ",
      refunded: "ریفنڈ ریکارڈ ہو گیا — اسٹاک اپڈیٹ ہو گیا۔",
      refunding: "ریفنڈ ہو رہا ہے...",
      refundedNote: "{total} میں سے {refunded} یونٹ ریفنڈ ہو چکے",
      refundConfirmTitle: "اس آئٹم کا ریفنڈ کریں",
      refundRemaining: "{total} میں سے {remaining} یونٹ ریفنڈ کے لیے باقی ہیں",
      refundQuantity: "مقدار",
      refundAmountOverride: "رقم (اختیاری)",
      refundMethod: "ریفنڈ کا طریقہ",
      methodCash: "نقد",
      methodCard: "کارڈ",
      methodStoreCredit: "اسٹور کریڈٹ",
      refundCondition: "آئٹم کی حالت",
      conditionResellable: "دوبارہ فروخت کے قابل",
      conditionDamaged: "خراب",
      refundReasonLabel: "وجہ (لازمی)",
      refundReasonPlaceholder: "مثلاً کسٹمر نے آئٹم واپس کر دیا",
      refundConfirmButton: "ریفنڈ کی تصدیق کریں",
      refundSlipTitle: "ریفنڈ سلپ",
      refundNo: "ریفنڈ نمبر",
      refundedAmount: "ریفنڈ شدہ",
      refundCustomerOptionalHint: "اختیاری — صرف اپنی ٹریکنگ کے لیے۔ استعمال کے لیے نیچے والا واؤچر کوڈ چاہیے، یہاں کسٹمر منتخب کرنا ضروری نہیں۔",
      voucherCodeHint: "واؤچر کوڈ {code} — کسی بھی آئندہ آمد پر استعمال کے لیے یہ پیش کریں۔",
    },
    storeCredit: {
      title: "اسٹور کریڈٹ",
      totalOutstanding: "کل باقی رقم",
      code: "کوڈ",
      taggedCustomer: "ٹیگ شدہ کسٹمر",
      notTagged: "—",
      issued: "جاری شدہ",
      balance: "بیلنس",
      empty: "ابھی تک کوئی اسٹور کریڈٹ واؤچر باقی نہیں۔",
      date: "تاریخ",
      amount: "رقم",
      appliedTo: "کہاں استعمال ہوا",
      loadingHistory: "ہسٹری لوڈ ہو رہی ہے...",
      noRedemptions: "ابھی تک استعمال نہیں ہوا۔",
    },
    paymentMediums: {
      title: "ادائیگی کے ذرائع",
      pendingSectionTitle: "زیر التوا بینک ٹرانسفرز",
      empty: "کوئی زیر التوا بینک ٹرانسفر ادائیگی نہیں۔",
      reference: "حوالہ نمبر",
      amount: "رقم",
      created: "بنائی گئی",
      status: "حیثیت",
      statusAwaiting: "ادائیگی کا انتظار",
      statusConfirmed: "تصدیق شدہ",
      statusCancelled: "منسوخ شدہ",
      statusAmbiguous: "جائزے کی ضرورت",
      markAsPaid: "ادا شدہ نشان زد کریں",
      cancelPayment: "منسوخ کریں",
      keepWaiting: "انتظار جاری رکھیں",
      cancelReasonPrompt: "منسوخ کرنے کی وجہ (اختیاری):",
      confirmError: "تصدیق نہیں ہو سکی — اسٹاک/واؤچر بیلنس چیک کر کے دوبارہ کوشش کریں۔",
      paidToast: "ادا شدہ نشان زد ہو گیا",
      cancelledToast: "ادائیگی منسوخ ہو گئی",
      lastError: "پچھلی کوشش ناکام ہوئی",
      ambiguousExplanation: "ایک ادائیگی کی اطلاع اسی رقم کی دو زیر التوا ادائیگیوں سے ملی — منتخب کریں کونسی اصل میں ادا ہوئی",
      forwarderStale: "خودکار ادائیگی کی شناخت شاید بند ہے",
      forwarderLastSeen: "فون آخری بار کب رابطے میں تھا",
    },
    shifts: {
      title: "شفٹس",
      openShift: "شفٹ کھولیں",
      openingFloat: "ابتدائی رقم",
      openingFloatHint: "وہ نقدی جس کے ساتھ آپ یہ شفٹ شروع کر رہے ہیں",
      openShiftSubmit: "شفٹ کھولیں",
      openShiftSuccess: "شفٹ کھل گئی",
      currentShift: "موجودہ شفٹ",
      openedAt: "کھلنے کا وقت",
      openedBy: "کس نے کھولی",
      recordCashMovement: "نقدی کی نقل و حرکت درج کریں",
      cashMovementAmount: "رقم",
      cashMovementAmountHint: "دراز میں نقدی ڈالنے کے لیے مثبت، نکالنے کے لیے منفی نمبر",
      cashMovementReason: "وجہ",
      cashMovementContact: "متعلقہ رابطہ (اختیاری)",
      cashMovementSubmit: "درج کریں",
      cashMovementSuccess: "نقدی کی نقل و حرکت درج ہو گئی",
      closeShift: "شفٹ بند کریں",
      expectedCash: "متوقع نقدی",
      countedCash: "گنی گئی نقدی",
      countedCashHint: "دراز گنیں اور کل رقم درج کریں",
      closeShiftNotes: "نوٹس (اختیاری)",
      closeShiftSubmit: "شفٹ بند کریں",
      closeShiftSuccess: "شفٹ بند ہو گئی",
      variance: "فرق",
      varianceMatch: "برابر",
      varianceShort: "کم",
      varianceOver: "زیادہ",
      noOpenShift: "کوئی شفٹ کھلی نہیں",
      history: "شفٹ کی تاریخ",
      noHistory: "ابھی تک کوئی شفٹ نہیں۔",
      status: "حالت",
      statusOpen: "کھلی",
      statusClosed: "بند",
      viewDetail: "دیکھیں",
      alreadyHaveOpenShift: "آپ کی پہلے سے ایک شفٹ کھلی ہے — نئی کھولنے سے پہلے اسے بند کریں۔",
      noOpenShiftBanner: "کوئی شفٹ کھلی نہیں — بیچنے کے لیے پہلے شفٹ کھولیں۔",
      statusNeedsReview: "جائزے کی ضرورت",
      reconcile: "ریکنسائل کریں",
      reconcileSubmit: "گنتی محفوظ کریں",
      reconcileSuccess: "شفٹ ریکنسائل ہو گئی",
      autoClosedExplanation:
        "یہ شفٹ بغیر کسی سرگرمی کے کھلی رہ جانے کی وجہ سے خودکار طور پر بند ہوئی — ابھی تک کسی نے دراز نہیں گنا۔ اب گنیں اور اصل رقم درج کریں۔",
      closedAt: "بند ہوئی",
      filterAllStatuses: "تمام حالتیں",
      filterAllUsers: "تمام عملہ",
      filterOnlyVariance: "صرف فرق والی شفٹس",
      filterMinVariance: "کم از کم فرق",
      filterMaxVariance: "زیادہ سے زیادہ فرق",
      loadingDetail: "شفٹ کی تفصیلات لوڈ ہو رہی ہیں...",
      detailSales: "سیلز",
      detailRefunds: "ریفنڈز",
      detailCashMovements: "نقدی کی نقل و حرکت",
      detailEmpty: "کوئی نہیں",
    },
    report: {
      title: "سیلز رپورٹ",
      loading: "ڈیٹا لوڈ ہو رہا ہے...",
      period: "مدت",
      filterLabel: "فلٹر",
      generatedOn: "تیار کردہ بتاریخ",
      allProducts: "تمام پروڈکٹس",
      profitableProducts: "منافع بخش پروڈکٹس",
      lossProducts: "نقصان دہ پروڈکٹس",
      paymentMedium: "ادائیگی کا ذریعہ",
      allPaymentMediums: "تمام ادائیگی کے ذرائع",
      paymentMediumBreakdown: "ادائیگی کے ذریعے کے مطابق",
      unrecordedMedium: "غیر ریکارڈ شدہ",
      shrinkageTitle: "کمی/نقصان",
      shrinkageTooltip:
        "وہ اسٹاک جو بغیر فروخت کے انوینٹری سے نکل گیا — نقصان، میعاد ختم، چوری، یا فزیکل گنتی کی تصحیح۔ یہ انوینٹری کے 'Adjust Stock' آپشن سے ریکارڈ ہوتا ہے، چیک آؤٹ سے نہیں۔",
      shrinkageUnitsLost: "ضائع شدہ یونٹس",
      shrinkageCostImpact: "لاگت کا اثر",
      shrinkageByReason: "وجہ کے مطابق",
      shrinkageByProduct: "پروڈکٹ کے مطابق",
      shrinkageEmpty: "اس مدت میں کوئی اسٹاک ایڈجسٹمنٹ درج نہیں ہوئی۔",
      viewDetail: "تفصیل دیکھیں",
      presetToday: "آج",
      presetYesterday: "کل",
      presetLast7: "پچھلے 7 دن",
      presetThisMonth: "اس مہینے",
      presetLastMonth: "پچھلے مہینے",
      printReport: "رپورٹ پرنٹ کریں",
      netSales: "خالص فروخت",
      grossProfit: "مجموعی منافع",
      marginOf: "{n}% منافع",
      transactions: "رسیدیں",
      averageSale: "اوسط سیل",
      itemsSold: "فروخت شدہ آئٹمز",
      refunds: "ریفنڈز",
      costOfGoods: "مال کی لاگت",
      voided: "منسوخ",
      vsPrevious: "پچھلے دورانیے کے مقابلے",
      vsPreviousNote: "فیصد تبدیلی ہر عدد کا اسی لمبائی کے پچھلے دورانیے سے موازنہ ہے۔",
      noPrevious: "موازنہ نہیں: اتنے ہی پچھلے دورانیے میں کوئی سیل نہیں ہوئی۔",
      localHistory: "آف لائن: صرف اس رجسٹر میں محفوظ سیلز دکھائی جا رہی ہیں، {date} سے۔ انٹرنیٹ سے جڑنے پر پرانی سیلز بھی نظر آئیں گی۔",
      localHistoryEmpty: "آف لائن: صرف اس رجسٹر میں محفوظ سیلز دکھائی جا رہی ہیں۔ انٹرنیٹ سے جڑنے پر پرانی سیلز بھی نظر آئیں گی۔",
      localHistorySending: "یہ رجسٹر ابھی اپنی تازہ سیلز بھیج رہا ہے، اس لیے صرف اس میں محفوظ سیلز دکھائی جا رہی ہیں۔ پوری ہسٹری کے لیے تھوڑی دیر بعد ریفریش کریں۔",
      grossDetail: "ریفنڈ سے پہلے {amount}",
      refundsDetail: "{count} ریفنڈز · {items} آئٹمز",
      voidsDetail: "{n} سیلز منسوخ",
      itemsReturned: "{n} واپس",
      perReceipt: "فی رسید",
      salesByCategory: "کیٹیگری کے لحاظ سے فروخت",
      salesByCashier: "کیشیئر کے لحاظ سے فروخت",
      category: "کیٹیگری",
      cashier: "کیشیئر",
      share: "حصہ",
      sales: "سیلز",
      unassigned: "نامعلوم",
      productPerformance: "پروڈکٹ کارکردگی",
      searchProducts: "پروڈکٹ تلاش کریں",
      allCategories: "تمام کیٹیگریز",
      product: "پروڈکٹ",
      sold: "فروخت",
      returned: "واپس",
      avgPrice: "اوسط قیمت",
      cost: "لاگت",
      profit: "منافع",
      margin: "مارجن",
      total: "کل",
      noSalesInPeriod: "اس دورانیے میں کوئی سیل نہیں۔",
      showing: "{total} میں سے {from}–{to} پروڈکٹس",
      trendTab: "رجحان",
      hoursTab: "اوقات",
      daysTab: "دن",
      salesTrend: "نیٹ سیلز اور منافع",
      trendHint_day: "روزانہ۔ ریفنڈ اسی دن میں گنا جاتا ہے جس دن دیا گیا۔",
      trendHint_week: "ہفتہ وار (پیر سے)۔ ریفنڈ اسی ہفتے میں گنا جاتا ہے جس میں دیا گیا۔",
      trendHint_month: "ماہانہ۔ ریفنڈ اسی مہینے میں گنا جاتا ہے جس میں دیا گیا۔",
      busiestHours: "مصروف ترین اوقات",
      busiestHoursHint: "دن کے ہر گھنٹے کی نیٹ سیلز، دورانیے کے تمام دن ملا کر۔",
      busiestDays: "ہفتے کے مصروف ترین دن",
      busiestDaysHint: "ہفتے کے ہر دن کی نیٹ سیلز، دورانیے کے تمام ہفتے ملا کر۔",
    },
    notices: {
      dueSoon: "آپ کی سبسکرپشن {date} کو ختم ہو رہی ہے ({days} دن باقی)۔ براہ کرم وقت پر تجدید کریں تاکہ ایپ چلتی رہے۔",
      dueToday: "آپ کی سبسکرپشن آج ختم ہو رہی ہے۔ براہ کرم تجدید کریں تاکہ ایپ چلتی رہے۔",
      overdue: "آپ کی سبسکرپشن {date} کو ختم ہو چکی ہے اور ادائیگی {days} دن سے واجب الادا ہے۔ رکاوٹ سے بچنے کے لیے اپنے فراہم کنندہ کو ادائیگی کریں۔",
      dismiss: "بند کریں",
    },
    notFound: {
      title: "صفحہ نہیں ملا",
      desc: "جو صفحہ آپ ڈھونڈ رہے ہیں وہ موجود نہیں یا منتقل ہو چکا ہے۔",
      backHome: "ہوم پر واپس جائیں",
    },
  },
};

export default translations;
